<script>
import AttributeCategoryInput from './AttributeCategoryInput.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import { useVuelidate } from '@vuelidate/core';
import { useAlert } from 'dashboard/composables';
import { required, minLength } from '@vuelidate/validators';
import { getRegexp, normalizeRegexPattern } from 'shared/helpers/Validators';
import { ATTRIBUTE_TYPES, FORMULA_OPS } from './constants';
import NextButton from 'dashboard/components-next/button/Button.vue';
import TagInput from 'dashboard/components-next/taginput/TagInput.vue';
import { mapGetters } from 'vuex';

export default {
  components: {
    AttributeCategoryInput,
    ComboBox,
    NextButton,
    TagInput,
  },
  props: {
    selectedAttribute: {
      type: Object,
      default: () => {},
    },
    isUpdating: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['onClose'],
  setup() {
    return { v$: useVuelidate() };
  },
  data() {
    return {
      displayName: '',
      description: '',
      attributeType: 0,
      regexPattern: null,
      regexCue: null,
      regexEnabled: false,
      show: true,
      attributeKey: '',
      values: [],
      tagInputTouched: false,
      featured: false,
      category: '',
      formulaEnabled: false,
      formulaOp: 'sum',
      formulaSourceKey: '',
    };
  },
  validations: {
    displayName: { required },
    attributeType: { required },
    description: { required, minLength: minLength(1) },
    attributeKey: {
      required,
      isKey(value) {
        return !(value.indexOf(' ') >= 0);
      },
    },
  },
  computed: {
    ...mapGetters({
      getAttributesByModel: 'attributes/getAttributesByModel',
    }),
    categoryOptions() {
      const model =
        this.selectedAttribute.attribute_model || 'conversation_attribute';
      const defs = this.getAttributesByModel(model) || [];
      return [
        ...new Set(
          defs.map(def => (def.category || '').trim()).filter(Boolean)
        ),
      ].sort((a, b) => a.localeCompare(b));
    },
    types() {
      return ATTRIBUTE_TYPES.map(item => ({
        ...item,
        option: this.$t(`ATTRIBUTES_MGMT.ATTRIBUTE_TYPES.${item.key}`),
      }));
    },
    formulaOps() {
      return FORMULA_OPS.map(item => ({
        ...item,
        option: this.$t(`ATTRIBUTES_MGMT.FORMULA.OP.${item.key}`),
      }));
    },
    isContactModel() {
      return (
        this.selectedAttribute.attribute_model === 'contact_attribute' ||
        this.selectedAttribute.attribute_model === 1
      );
    },
    conversationNumericAttributes() {
      const defs = this.getAttributesByModel('conversation_attribute') || [];
      return defs.filter(
        def =>
          ['number', 'currency'].includes(def.attribute_display_type) &&
          def.attribute_key !== this.attributeKey
      );
    },
    setAttributeListValue() {
      return this.selectedAttribute.attribute_values || [];
    },
    updatedAttributeListValues() {
      return this.values;
    },
    isButtonDisabled() {
      return this.v$.description.$invalid || this.isTagInputEmpty;
    },
    isTagInputEmpty() {
      return this.isAttributeTypeList && this.values.length === 0;
    },
    isTagInputInvalid() {
      return this.tagInputTouched && this.isTagInputEmpty;
    },

    pageTitle() {
      return `${this.$t('ATTRIBUTES_MGMT.EDIT.TITLE')} - ${
        this.selectedAttribute.attribute_display_name
      }`;
    },
    selectedAttributeType() {
      return this.types.find(
        item =>
          item.key.toLowerCase() ===
          this.selectedAttribute.attribute_display_type
      )?.id;
    },
    keyErrorMessage() {
      if (!this.v$.attributeKey.isKey) {
        return this.$t('ATTRIBUTES_MGMT.ADD.FORM.KEY.IN_VALID');
      }
      return this.$t('ATTRIBUTES_MGMT.ADD.FORM.KEY.ERROR');
    },
    isAttributeTypeList() {
      return this.attributeType === 6 || this.attributeType === 9;
    },
    isAttributeTypeText() {
      return this.attributeType === 0;
    },
    isRegexEnabled() {
      return this.regexEnabled;
    },
  },
  mounted() {
    this.setFormValues();
  },
  methods: {
    onClose() {
      this.$emit('onClose');
    },
    setFormValues() {
      const regexPattern = this.selectedAttribute.regex_pattern
        ? getRegexp(this.selectedAttribute.regex_pattern).toString()
        : null;
      this.displayName = this.selectedAttribute.attribute_display_name;
      this.description = this.selectedAttribute.attribute_description;
      this.attributeType = this.selectedAttributeType;
      this.attributeKey = this.selectedAttribute.attribute_key;
      this.regexPattern = regexPattern;
      this.regexCue = this.selectedAttribute.regex_cue;
      this.regexEnabled = regexPattern != null;
      this.values = this.setAttributeListValue;
      this.featured = !!this.selectedAttribute.featured;
      this.category = this.selectedAttribute.category || '';
      const formula = this.selectedAttribute.formula;
      this.formulaEnabled = !!formula?.op;
      this.formulaOp = formula?.op || 'sum';
      this.formulaSourceKey = formula?.source_attribute_key || '';
    },
    async editAttributes() {
      this.v$.$touch();
      if (this.v$.$invalid) {
        return;
      }
      if (!this.regexEnabled) {
        this.regexPattern = null;
        this.regexCue = null;
      }
      try {
        await this.$store.dispatch('attributes/update', {
          id: this.selectedAttribute.id,
          attribute_description: this.description,
          attribute_display_name: this.displayName,
          attribute_values: this.updatedAttributeListValues,
          regex_pattern: normalizeRegexPattern(this.regexPattern),
          regex_cue: this.regexCue,
          featured: this.featured,
          category: this.category,
          formula:
            this.isContactModel && this.formulaEnabled && this.formulaSourceKey
              ? {
                  op: this.formulaOp,
                  source_attribute_key: this.formulaSourceKey,
                  source_model: 'conversation',
                }
              : null,
        });
        this.alertMessage = this.$t('ATTRIBUTES_MGMT.EDIT.API.SUCCESS_MESSAGE');
        this.onClose();
      } catch (error) {
        const errorMessage = error?.message;
        this.alertMessage =
          errorMessage || this.$t('ATTRIBUTES_MGMT.EDIT.API.ERROR_MESSAGE');
      } finally {
        useAlert(this.alertMessage);
      }
    },
    toggleRegexEnabled() {
      this.regexEnabled = !this.regexEnabled;
    },
  },
};
</script>

<template>
  <div class="flex flex-col h-auto overflow-auto">
    <woot-modal-header :header-title="pageTitle" />
    <form class="flex flex-col w-full" @submit.prevent="editAttributes">
      <div class="w-full">
        <woot-input
          v-model="displayName"
          :label="$t('ATTRIBUTES_MGMT.ADD.FORM.NAME.LABEL')"
          type="text"
          :class="{ error: v$.displayName.$error }"
          :error="
            v$.displayName.$error
              ? $t('ATTRIBUTES_MGMT.ADD.FORM.NAME.ERROR')
              : ''
          "
          :placeholder="$t('ATTRIBUTES_MGMT.ADD.FORM.NAME.PLACEHOLDER')"
          @blur="v$.displayName.$touch"
        />
        <woot-input
          v-model="attributeKey"
          :label="$t('ATTRIBUTES_MGMT.ADD.FORM.KEY.LABEL')"
          type="text"
          :class="{ error: v$.attributeKey.$error }"
          :error="v$.attributeKey.$error ? keyErrorMessage : ''"
          :placeholder="$t('ATTRIBUTES_MGMT.ADD.FORM.KEY.PLACEHOLDER')"
          readonly
          @blur="v$.attributeKey.$touch"
        />
        <label :class="{ error: v$.description.$error }">
          {{ $t('ATTRIBUTES_MGMT.ADD.FORM.DESC.LABEL') }}
          <textarea
            v-model="description"
            rows="5"
            type="text"
            :placeholder="$t('ATTRIBUTES_MGMT.ADD.FORM.DESC.PLACEHOLDER')"
            @blur="v$.description.$touch"
          />
          <span v-if="v$.description.$error" class="message">
            {{ $t('ATTRIBUTES_MGMT.ADD.FORM.DESC.ERROR') }}
          </span>
        </label>
        <label class="block mb-4" :class="{ error: v$.attributeType.$error }">
          {{ $t('ATTRIBUTES_MGMT.ADD.FORM.TYPE.LABEL') }}
          <ComboBox
            v-model="attributeType"
            teleport
            disabled
            :allow-deselect="false"
            :options="
              types.map(type => ({ value: type.id, label: type.option }))
            "
            data-testid="attribute-type"
          />
          <span v-if="v$.attributeType.$error" class="message">
            {{ $t('ATTRIBUTES_MGMT.ADD.FORM.TYPE.ERROR') }}
          </span>
        </label>
        <div v-if="isAttributeTypeList" class="mb-4">
          <label class="mb-1 block">
            {{ $t('ATTRIBUTES_MGMT.EDIT.TYPE.LIST.LABEL') }}
          </label>
          <div
            class="rounded-xl border px-3 py-2"
            :class="isTagInputInvalid ? 'border-n-ruby-9' : 'border-n-weak'"
          >
            <TagInput
              v-model="values"
              :placeholder="
                $t('ATTRIBUTES_MGMT.ADD.FORM.TYPE.LIST.PLACEHOLDER')
              "
              allow-create
              @blur="tagInputTouched = true"
            />
          </div>
          <label
            v-show="isTagInputInvalid"
            class="text-n-ruby-9 dark:text-n-ruby-9 text-sm font-normal mt-1"
          >
            {{ $t('ATTRIBUTES_MGMT.ADD.FORM.TYPE.LIST.ERROR') }}
          </label>
        </div>
        <div v-if="isAttributeTypeText" class="mb-4">
          <label class="flex items-center gap-2">
            <input
              v-model="regexEnabled"
              type="checkbox"
              @input="toggleRegexEnabled"
            />
            {{ $t('ATTRIBUTES_MGMT.ADD.FORM.ENABLE_REGEX.LABEL') }}
          </label>
        </div>
        <woot-input
          v-if="isAttributeTypeText && isRegexEnabled"
          v-model="regexPattern"
          :label="$t('ATTRIBUTES_MGMT.ADD.FORM.REGEX_PATTERN.LABEL')"
          type="text"
          :placeholder="
            $t('ATTRIBUTES_MGMT.ADD.FORM.REGEX_PATTERN.PLACEHOLDER')
          "
        />
        <woot-input
          v-if="isAttributeTypeText && isRegexEnabled"
          v-model="regexCue"
          :label="$t('ATTRIBUTES_MGMT.ADD.FORM.REGEX_CUE.LABEL')"
          type="text"
          :placeholder="$t('ATTRIBUTES_MGMT.ADD.FORM.REGEX_CUE.PLACEHOLDER')"
        />
        <AttributeCategoryInput
          v-model="category"
          class="mb-2"
          :label="$t('ATTRIBUTES_MGMT.FORM.CATEGORY.LABEL')"
          :help="$t('ATTRIBUTES_MGMT.FORM.CATEGORY.HELP')"
          :placeholder="$t('ATTRIBUTES_MGMT.FORM.CATEGORY.PLACEHOLDER')"
          :options="categoryOptions"
        />
        <div class="mb-4">
          <label class="flex items-center gap-2">
            <input v-model="featured" type="checkbox" />
            {{ $t('ATTRIBUTES_MGMT.FEATURED.LABEL') }}
            <span
              v-tooltip.top="$t('ATTRIBUTES_MGMT.FEATURED.HELP')"
              class="i-lucide-info size-3.5 shrink-0 text-n-slate-10"
              role="img"
              :aria-label="$t('ATTRIBUTES_MGMT.FEATURED.HELP')"
            />
          </label>
        </div>
        <div v-if="isContactModel" class="mb-4">
          <label class="flex items-center gap-2 mb-2">
            <input v-model="formulaEnabled" type="checkbox" />
            {{ $t('ATTRIBUTES_MGMT.FORMULA.ENABLE') }}
            <span
              v-tooltip.top="$t('ATTRIBUTES_MGMT.FORMULA.HELP')"
              class="i-lucide-info size-3.5 shrink-0 text-n-slate-10"
              role="img"
              :aria-label="$t('ATTRIBUTES_MGMT.FORMULA.HELP')"
            />
          </label>
          <template v-if="formulaEnabled">
            <label>
              {{ $t('ATTRIBUTES_MGMT.FORMULA.OP.LABEL') }}
              <ComboBox
                v-model="formulaOp"
                teleport
                :allow-deselect="false"
                :options="[
                  ...formulaOps.map(op => ({ value: op.id, label: op.option })),
                ]"
              />
            </label>
            <label>
              {{ $t('ATTRIBUTES_MGMT.FORMULA.SOURCE.LABEL') }}
              <ComboBox
                v-model="formulaSourceKey"
                teleport
                :allow-deselect="false"
                :options="[
                  {
                    value: '',
                    label: $t('ATTRIBUTES_MGMT.FORMULA.SOURCE.PLACEHOLDER'),
                  },
                  ...conversationNumericAttributes.map(attr => ({
                    value: attr.attribute_key,
                    label: attr.attribute_display_name,
                  })),
                ]"
              />
            </label>
          </template>
        </div>
      </div>
      <div class="flex flex-row justify-end w-full gap-2 px-0 py-2">
        <NextButton
          faded
          slate
          type="reset"
          :label="$t('ATTRIBUTES_MGMT.ADD.CANCEL_BUTTON_TEXT')"
          @click.prevent="onClose"
        />
        <NextButton
          type="submit"
          :label="$t('ATTRIBUTES_MGMT.EDIT.UPDATE_BUTTON_TEXT')"
          :is-loading="isUpdating"
          :disabled="isButtonDisabled"
        />
      </div>
    </form>
  </div>
</template>

<style lang="scss" scoped>
.key-value {
  padding: 0 0.5rem 0.5rem 0;
  font-family: monospace;
}
</style>
